import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserMetadata } from '../../context/UserMetadataContext';
import apiService from '../Profile360/services/apiService';
import { Box, Typography, CircularProgress, Chip, Stack } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonOffIcon from '@mui/icons-material/PersonOff';

const cardConfig = [
  { key: 'privilegeCreep', label: 'Privilege Creep', icon: ShieldIcon, borderColor: '#d27373', tabIndex: 1 },
  { key: 'excessiveAccess', label: 'Excessive Access', icon: TrendingUpIcon, borderColor: '#f59e0b', tabIndex: 2 },
  { key: 'suspiciousActivity', label: 'Suspicious Activity', icon: WarningAmberIcon, borderColor: '#3b82f6', tabIndex: 3 },
  { key: 'dormantUsers', label: 'Dormant Users', icon: PersonOffIcon, borderColor: '#475569', tabIndex: 4 },
];

/* ── Shared sub-card style (matches alert cards) ───────────────── */
const metricCardSx = (borderColor) => ({
  p: 2,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  borderLeft: 3,
  borderLeftColor: borderColor,
  bgcolor: 'background.subtle',
  cursor: 'pointer',
  transition: 'all 0.15s',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  '&:hover': {
    borderColor: 'rgba(99, 102, 241, 0.3)',
    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'action.hover',
  },
});

const Profile360Summary = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { userMetadata } = useUserMetadata();

  const [loading, setLoading] = useState(true);
  const [overallRiskScore, setOverallRiskScore] = useState(0);
  const [riskCategories, setRiskCategories] = useState({
    privilegeCreep: { count: 0, critical: 0, high: 0 },
    excessiveAccess: { count: 0, critical: 0, high: 0 },
    suspiciousActivity: { count: 0, high: 0, medium: 0 },
    dormantUsers: { count: 0, highRisk: 0 },
  });

  const tenantName = userMetadata?.company || 'trustx';
  const baseUrl = process.env.REACT_APP_API_URL || 'https://api-dev.trustx.cloud/profile360';

  const fetchRiskData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      apiService.setAuthToken(token);
      apiService.setTenant(tenantName);

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tenant-Name': tenantName,
      };

      const [pcRes, eaRes, duRes, saRes] = await Promise.all([
        fetch(`${baseUrl}/api/privilege-creep/summary`, { headers }),
        fetch(`${baseUrl}/api/excessive-access/summary`, { headers }),
        apiService.getAIDormantInsights({ page: 1, pageSize: 1 }),
        fetch(`${baseUrl}/api/suspicious-activity/summary`, { headers }).catch(() => null),
      ]);

      const pcData = await pcRes.json();
      const eaData = await eaRes.json();
      const saData = saRes ? await saRes.json() : null;

      const pcSummary = pcData.success ? pcData.data?.summary || {} : {};
      const pcCritical = pcSummary.critical?.count || 0;
      const pcHigh = pcSummary.high?.count || 0;

      const eaSummary = eaData.success ? eaData.data || {} : {};
      const eaCritical = eaSummary.critical_count || 0;
      const eaHigh = eaSummary.high_count || 0;
      const eaModerate = eaSummary.moderate_count || 0;

      const duTotal = duRes.success ? duRes.data?.total || 0 : 0;

      const saHigh = saData?.success ? saData.data?.high_risk_count || 0 : 0;
      const saMedium = saData?.success ? saData.data?.medium_risk_count || 0 : 0;

      setRiskCategories({
        privilegeCreep: { count: pcCritical + pcHigh, critical: pcCritical, high: pcHigh },
        excessiveAccess: { count: eaCritical + eaHigh + eaModerate, critical: eaCritical, high: eaHigh },
        suspiciousActivity: { count: saHigh + saMedium, high: saHigh, medium: saMedium },
        dormantUsers: { count: duTotal, highRisk: duTotal },
      });

      const totalRisks = pcCritical + pcHigh + eaCritical + eaHigh + eaModerate + saHigh + saMedium + duTotal;
      const weightedScore =
        pcCritical * 10 + pcHigh * 7 + eaCritical * 10 + eaHigh * 7 + eaModerate * 4 + saHigh * 8 + saMedium * 5 + duTotal * 3;
      const maxPossibleScore = totalRisks * 10;
      const riskScore = maxPossibleScore > 0 ? Math.min(100, Math.round((weightedScore / maxPossibleScore) * 100)) : 0;
      setOverallRiskScore(riskScore);
    } catch (err) {
      console.error('Error fetching risk data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, tenantName, baseUrl]);

  useEffect(() => {
    fetchRiskData();
  }, [fetchRiskData]);

  const getRiskLevel = (score) => {
    if (score >= 80) return { text: 'Critical', color: '#d27373' };
    if (score >= 60) return { text: 'High', color: '#f59e0b' };
    if (score >= 40) return { text: 'Moderate', color: '#f59e0b' };
    if (score >= 20) return { text: 'Low', color: '#5da87a' };
    return { text: 'Minimal', color: '#5da87a' };
  };

  const riskLevel = getRiskLevel(overallRiskScore);

  const handleCardClick = (tabIndex) => {
    navigate('/profile360/analytics', { state: { activeTab: tabIndex } });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2, flex: 1 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">Loading risk data...</Typography>
      </Box>
    );
  }

  const breakdownText = (cat) => {
    const parts = [];
    if (cat.critical > 0) parts.push({ label: `${cat.critical} critical`, color: 'error.main' });
    if (cat.high > 0) parts.push({ label: `${cat.high} high`, color: 'warning.main' });
    if (cat.medium > 0) parts.push({ label: `${cat.medium} medium`, color: 'warning.light' });
    if (cat.highRisk > 0 && !cat.critical && !cat.high) parts.push({ label: 'Inactive accounts', color: 'text.secondary' });
    return parts;
  };

  return (
    <Box sx={{ display: 'flex', gap: 2.5, flex: 1 }}>
      {/* Risk Score Gauge */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 120 }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            border: `4px solid ${riskLevel.color}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <Typography sx={{ color: riskLevel.color, fontWeight: 700, fontSize: '1.75rem', lineHeight: 1 }}>
            {overallRiskScore}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Risk Score
          </Typography>
        </Box>
        <Chip
          label={`${riskLevel.text}`}
          size="small"
          sx={{ mt: 1, bgcolor: riskLevel.color, color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 22 }}
        />
      </Box>

      {/* Risk Metric Cards */}
      <Grid2 container spacing={1.5} sx={{ flex: 1 }}>
        {cardConfig.map((cfg) => {
          const cat = riskCategories[cfg.key];
          const Icon = cfg.icon;
          const parts = breakdownText(cat);
          return (
            <Grid2 key={cfg.key} xs={6}>
              <Box onClick={() => handleCardClick(cfg.tabIndex)} sx={metricCardSx(cfg.borderColor)}>
                <Icon sx={{ color: cfg.borderColor, fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.78rem', color: 'text.secondary' }}>
                  {cfg.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>{cat.count}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {parts.map((p, i) => (
                    <Typography key={i} variant="caption" sx={{ color: p.color, fontSize: '0.68rem' }}>
                      {p.label}
                    </Typography>
                  ))}
                </Stack>
                <Typography variant="caption" color="primary.main" fontWeight={500} sx={{ mt: 'auto' }}>
                  View &rarr;
                </Typography>
              </Box>
            </Grid2>
          );
        })}
      </Grid2>
    </Box>
  );
};

export default Profile360Summary;
